import React, { useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowLeft, ArrowRight, Check, Lock, Loader2, Info } from 'lucide-react';
import { puertoRicoCities, unitedStatesStates } from '../data/locations';
import { generateEventId, trackLead, type MetaLeadUser } from '../utils/meta';
import { getStoredAttribution } from '../utils/captureAttribution';
const CALENDLY_URL = 'https://calendly.com/vantixcredit/diagnostico-de-credito';
// Lead delivery endpoint.
// Using the raw email forces FormSubmit to re-send the activation email
// whenever the page is served from a new origin. Replace this value with the
// permanent alias token from the FormSubmit activation email
// (e.g. 'a1b2c3d4e5f6...') to keep delivery active forever.
const FORMSUBMIT_TARGET = 'eduardoar13@gmail.com';
type Step = {
  id: string;
  title: string;
  // Optional lead-in paragraph shown above the question
  context?: string;
  subtitle?: string;
  type: 'single' | 'multi';
  options: string[];
};
const steps: Step[] = [{
  id: 'negatives',
  title: '¿Qué cuentas negativas tiene tu crédito?',
  subtitle: 'Selecciona todo lo que aplique. No te preocupes, lo arreglamos.',
  type: 'multi',
  options: ['Colecciones', 'Pagos tarde', 'Charge off', 'Hard inquiries', 'Reposición de vehículo', 'No lo tengo claro']
}, {
  id: 'goals',
  title: '¿Para qué quieres reparar tu crédito?',
  subtitle: 'Selecciona todas las que apliquen para darte el mejor plan.',
  type: 'multi',
  options: ['🏡  Comprar una casa', '🚗  Comprar un carro', '💼  Capital para mi negocio', '💵  Buscar un préstamo personal']
}, {
  id: 'priority',
  title: '¿Qué tan importante es reparar tu crédito para ti ahora mismo?',
  type: 'single',
  options: ['Es importante solucionarlo, se ha vuelto mi prioridad', 'Solo estoy explorando opciones']
}, {
  id: 'attempts',
  title: '¿Has intentado reparar tu crédito antes por tu cuenta o con otra compañía?',
  type: 'single',
  options: ['Lo intenté por mí mismo, pero no lo logré', 'Lo intenté con otra compañía pero sin resultados', 'Ninguna de las anteriores']
}, {
  id: 'budget',
  context: 'La sesión de consulta es gratuita, pero los servicios de Vantix tienen un valor de $499.99 USD en adelante. Si en la sesión de consultoría encontramos la manera de ayudarte y entregarte los resultados que buscas…',
  title: '¿Cuentas con este mínimo de inversión?',
  type: 'single',
  options: ['Sí, me lo puedo permitir', 'No, no cuento con el monto mínimo']
}];
const TOTAL_STEPS = steps.length + 1;

// Answer that ends the flow: we only continue with people who can invest.
const BUDGET_STEP_ID = 'budget';
const DISQUALIFYING_ANSWER = 'No, no cuento con el monto mínimo';
type Contact = {
  name: string;
  phone: string;
  email: string;
  country: string;
  city: string;
};
const emptyContact: Contact = {
  name: '',
  phone: '',
  email: '',
  country: '',
  city: ''
};
export function QualificationForm() {
  const [step, setStep] = useState(0);
  const [single, setSingle] = useState<Record<string, string>>({});
  const [multi, setMulti] = useState<Record<string, string[]>>({});
  const [contact, setContact] = useState<Contact>(emptyContact);
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const [submissionError, setSubmissionError] = useState('');
  const [disqualified, setDisqualified] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const transitionLocked = useRef(false);
  const submissionLocked = useRef(false);
  const safeStep = Math.min(Math.max(step, 0), steps.length);
  const isContactStep = safeStep === steps.length;
  const current = steps[safeStep];
  const progress = Math.round((submitted ? TOTAL_STEPS : safeStep) / TOTAL_STEPS * 100);
  const cityOptions = contact.country === 'Puerto Rico' ? puertoRicoCities : contact.country === 'Estados Unidos' ? unitedStatesStates : [];
  const finishTransition = () => {
    transitionLocked.current = false;
    setIsTransitioning(false);
  };
  const handleSingle = (id: string, option: string) => {
    if (transitionLocked.current) return;
    transitionLocked.current = true;
    setIsTransitioning(true);
    setSingle(prev => ({
      ...prev,
      [id]: option
    }));
    window.setTimeout(() => {
      if (id === BUDGET_STEP_ID && option === DISQUALIFYING_ANSWER) {
        setDisqualified(true);
      } else {
        setStep(currentStep => Math.min(currentStep + 1, steps.length));
      }
      finishTransition();
    }, 220);
  };
  const handleContinue = () => {
    if (transitionLocked.current) return;
    transitionLocked.current = true;
    setIsTransitioning(true);
    window.setTimeout(() => {
      setStep(currentStep => Math.min(currentStep + 1, steps.length));
      finishTransition();
    }, 220);
  };
  const resetForm = () => {
    setStep(0);
    setSingle({});
    setMulti({});
    setContact(emptyContact);
    setSubmitted(false);
    setSubmissionError('');
    setDisqualified(false);
    finishTransition();
    submissionLocked.current = false;
  };
  const toggleMulti = (id: string, option: string) => {
    setMulti(prev => {
      const currentList = prev[id] ?? [];
      return {
        ...prev,
        [id]: currentList.includes(option) ? currentList.filter(o => o !== option) : [...currentList, option]
      };
    });
  };
  const contactValid = contact.name.trim().length > 2 && contact.phone.trim().length >= 7 && /\S+@\S+\.\S+/.test(contact.email) && contact.country !== '' && contact.city !== '';
  const labelClass = 'block text-[11px] font-semibold tracking-[0.18em] uppercase text-white/65 mb-2';
  const inputClass = 'w-full rounded-lg border border-white/12 bg-white/[0.03] px-4 py-3 text-[15px] text-white placeholder:text-white/25 font-normal outline-none transition-colors focus:border-gold';

  // Prefill Calendly with what they already told us so they don't retype it.
  const bookingUrl = `${CALENDLY_URL}?name=${encodeURIComponent(contact.name)}&email=${encodeURIComponent(contact.email)}`;
  const sendLead = async () => {
    if (submissionLocked.current) return;
    submissionLocked.current = true;
    setSending(true);
    setSubmissionError('');
    const [firstName = '', ...lastNameParts] = contact.name.trim().split(/\s+/);
    const lastName = lastNameParts.join(' ');
    const eventId = generateEventId();
    const metaUser: MetaLeadUser = {
      firstName,
      lastName,
      email: contact.email,
      phone: contact.phone,
      city: contact.country === 'Puerto Rico' ? contact.city : '',
      state: contact.country === 'Estados Unidos' ? contact.city : 'pr',
      country: contact.country
    };
    const payload: Record<string, string> = {
      _subject: `Nuevo formulario completado: ${contact.name}`,
      _template: 'box',
      _captcha: 'false',
      Mensaje: `Saludos Eduardo, el cliente ${contact.name} ha llenado el formulario exitosamente, acá está lo que contestó:`,
      'Nombre y apellido': contact.name,
      Teléfono: contact.phone,
      'Correo electrónico': contact.email,
      Ubicación: `${contact.city}, ${contact.country}`,
      'Meta Event ID': eventId
    };
    steps.forEach(s => {
      payload[s.title] = s.type === 'single' ? single[s.id] ?? '—' : (multi[s.id] ?? []).join(', ') || '—';
    });
    Object.entries(getStoredAttribution()).forEach(([key, value]) => {
      payload[key] = value;
    });
    try {
      const response = await fetch(`https://formsubmit.co/ajax/${FORMSUBMIT_TARGET}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify(payload)
      });
      if (!response.ok) {
        throw new Error('No se pudo entregar el formulario');
      }
      await Promise.race([trackLead(metaUser, eventId), new Promise<void>(resolve => window.setTimeout(resolve, 1500))]);
      setSubmitted(true);
      window.setTimeout(() => {
        window.location.href = bookingUrl;
      }, 600);
    } catch {
      setSubmissionError('No pudimos enviar tu información. Revisa tu conexión e inténtalo nuevamente.');
      submissionLocked.current = false;
    } finally {
      setSending(false);
    }
  };
  return <section id="calificar" className="bg-ink-900 pt-14 pb-20 md:py-28 px-4 sm:px-6 lg:px-8 border-t border-white/10" data-id="element-84" data-mp="1yybvnwn7ndd7">
      <div className="max-w-3xl mx-auto" data-id="element-85" data-mp="1p1pb78ysqobp">
        {/* Heading */}
        <motion.div initial={{
        opacity: 0,
        y: 24
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.3
      }} transition={{
        duration: 0.25,
        ease: [0.16, 1, 0.3, 1]
      }} className="text-center mb-9 md:mb-12" data-id="element-86" data-mp="1p4l7ytywr6yk">
          <p className="text-[11px] font-semibold tracking-[0.3em] uppercase text-gold mb-5" data-id="element-87" data-mp="1fewitf8jgip3">
            Evaluación preliminar
          </p>
          <h2 className="text-3xl md:text-[2.75rem] font-semibold text-white tracking-tight mb-4 leading-[1.18]" data-id="element-88" data-mp="1tcte88b9aj26">
            Veamos si podemos <span className="text-gold" data-id="element-89" data-mp="1odgqzmgh39z2">ayudarte</span>
          </h2>
          <p className="text-base md:text-lg text-white/80 font-normal max-w-lg mx-auto leading-relaxed" data-id="element-90" data-mp="233o2tjbz5uqs">
            Responde unas preguntas rápidas sobre tu caso. Con eso preparamos tu
            diagnóstico antes de la llamada.
          </p>
        </motion.div>

        {/* Card */}
        <motion.div initial={{
        opacity: 0,
        y: 24
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.2
      }} transition={{
        duration: 0.25,
        ease: [0.16, 1, 0.3, 1]
      }} className="rounded-2xl border border-white/10 bg-ink-850 px-5 py-7 sm:p-8 md:p-10" data-id="element-91" data-mp="284lgpjpyc2mh">
          {/* Progress */}
          <div className="mb-8" data-id="element-92" data-mp="1y4wepdi6gziu">
            <div className="flex items-center justify-between mb-3" data-id="element-93" data-mp="1y3v4tccwej2p">
              <span className="text-[11px] font-semibold tracking-[0.18em] uppercase text-white/65" data-id="element-94" data-mp="1t861cxxrci14">
                {submitted ? 'Completado' : disqualified ? 'Evaluación finalizada' : `Paso ${step + 1} de ${TOTAL_STEPS}`}
              </span>
              <span className="text-[11px] font-semibold tracking-wide text-gold" data-id="element-95" data-mp="ctxtm9hncd9j">
                {progress}%
              </span>
            </div>
            <div className="h-[3px] w-full rounded-full bg-white/10 overflow-hidden" data-id="element-96" data-mp="2kewhukbu67dn">
              <motion.div className="h-full rounded-full bg-gold" animate={{
              width: `${progress}%`
            }} transition={{
              duration: 0.25,
              ease: [0.23, 1, 0.32, 1]
            }} data-id="element-97" data-mp="2ofosni0cwkc1" />
            </div>
          </div>

          <AnimatePresence mode="wait" data-id="element-98" data-mp="334clvhmqchyt">
            {/* Not a fit: flow stops here */}
            {disqualified && <motion.div key="disqualified" initial={{
            opacity: 0,
            scale: 0.98
          }} animate={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0,
            scale: 0.98
          }} transition={{
            duration: 0.25,
            ease: [0.23, 1, 0.32, 1]
          }} className="text-center py-4" data-id="element-99" data-mp="3861z93xfd7q2">
                <div className="h-14 w-14 rounded-full border border-white/15 bg-white/[0.03] flex items-center justify-center mx-auto mb-6" data-id="element-100" data-mp="2l3gtdjfvaca7">
                  <Info className="h-6 w-6 text-white/60" strokeWidth={1.75} data-id="element-101" data-mp="2yzu2o5a8z7dl" />
                </div>

                <h3 className="text-xl md:text-[1.6rem] font-semibold text-white mb-4 leading-snug" data-id="element-102" data-mp="2u5gcchafzqfs">
                  Gracias por tu interés
                </h3>
                <p className="text-base text-white/85 font-normal leading-relaxed max-w-md mx-auto" data-id="element-103" data-mp="340hzj7l5qifq">
                  Lo sentimos, en este momento no cuentas con los requisitos
                  mínimos para comenzar el proceso con Vantix. Nuestros
                  servicios tienen una inversión desde $499.99 USD.
                </p>
                <p className="text-base text-white/85 font-normal leading-relaxed max-w-md mx-auto mt-4" data-id="element-104" data-mp="3roj98ju2o4wq">
                  Cuando estés en condiciones de hacer esta inversión, con gusto
                  te atenderemos.
                </p>

                <button onClick={() => {
              setDisqualified(false);
              setSingle(prev => {
                const next = {
                  ...prev
                };
                delete next[BUDGET_STEP_ID];
                return next;
              });
            }} className="mt-8 inline-flex items-center gap-2 text-sm text-white/65 hover:text-white transition-colors" data-id="element-105" data-mp="f68c8rkl2t3y">
                  <ArrowLeft className="h-4 w-4" data-id="element-106" data-mp="3dox5t4d8w535" />
                  Cambiar mi respuesta
                </button>
              </motion.div>}

            {/* Question steps */}
            {!submitted && !disqualified && !isContactStep && current && <motion.div key={current.id} initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -20
          }} transition={{
            duration: 0.25,
            ease: [0.23, 1, 0.32, 1]
          }} data-id="element-107" data-mp="34p0mli9mjyfh">
                {current.context && <p className="text-[15px] md:text-base text-white/90 font-normal leading-relaxed mb-5 pb-5 border-b border-white/10" data-id="element-108" data-mp="2ivuxu3grb0gk">
                    {current.context}
                  </p>}
                <h3 className="text-xl md:text-[1.6rem] font-semibold text-white mb-2 leading-snug" data-id="element-109" data-mp="28xf741ln5t7l">
                  {current.title}
                </h3>
                {current.subtitle ? <p className="text-[15px] text-white/75 font-normal mb-7" data-id="element-110" data-mp="773n08qhrm6g">
                    {current.subtitle}
                  </p> : <div className="mb-7" data-id="element-111" data-mp="25e98lbvwueo" />}

                <div className="space-y-2.5" data-id="element-112" data-mp="angdea5jpmr1">
                  {current.options.map(option => {
                const selected = current.type === 'single' ? single[current.id] === option : (multi[current.id] ?? []).includes(option);
                return <button key={option} onClick={() => current.type === 'single' ? handleSingle(current.id, option) : toggleMulti(current.id, option)} disabled={isTransitioning} className={`w-full flex items-center justify-between gap-4 text-left px-5 py-4 rounded-lg border transition-colors duration-150 ${selected ? 'border-gold bg-gold/10' : 'border-white/10 bg-white/[0.02] hover:border-white/25 hover:bg-white/[0.04]'}`} data-id="element-113" data-mp="ohqss0vnk4kf">
                        <span className="text-[15px] md:text-base text-white font-medium" data-id="element-114" data-mp="mgjtbzxq1u41">
                          {option}
                        </span>
                        <span className={`h-5 w-5 shrink-0 border flex items-center justify-center transition-colors duration-150 ${current.type === 'multi' ? 'rounded' : 'rounded-full'} ${selected ? 'border-gold bg-gold' : 'border-white/25'}`} data-id="element-115" data-mp="wfsj0glk0mkl">
                          {selected && <Check className="h-3 w-3 text-black" strokeWidth={3} data-id="element-116" data-mp="3m2i0svnt8v06" />}
                        </span>
                      </button>;
              })}
                </div>

                <div className="mt-8 flex items-center justify-between gap-4" data-id="element-117" data-mp="1mpt77qwth3vf">
                  {step > 0 ? <button onClick={() => setStep(s => Math.max(s - 1, 0))} disabled={isTransitioning} className="inline-flex items-center gap-2 text-sm text-white/65 hover:text-white transition-colors disabled:cursor-not-allowed disabled:opacity-40" data-id="element-118" data-mp="1ro34c723qejo">
                      <ArrowLeft className="h-4 w-4" data-id="element-119" data-mp="26d90fm5n7onc" />
                      Atrás
                    </button> : <span data-id="element-120" data-mp="uydctap0qcun" />}

                  {current.type === 'multi' && <button onClick={handleContinue} disabled={isTransitioning || (multi[current.id] ?? []).length === 0} className="inline-flex items-center gap-2 bg-gold hover:bg-gold-light text-black font-semibold text-[15px] px-6 py-3 rounded-lg transition-colors duration-200 disabled:opacity-25 disabled:cursor-not-allowed disabled:hover:bg-gold" data-id="element-121" data-mp="yzywpfzyzrec">
                      Continuar
                      <ArrowRight className="h-4 w-4" data-id="element-122" data-mp="29cq49sxu0bh8" />
                    </button>}
                </div>
              </motion.div>}

            {/* Contact step */}
            {!submitted && isContactStep && <motion.div key="contact" initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -20
          }} transition={{
            duration: 0.25,
            ease: [0.23, 1, 0.32, 1]
          }} data-id="element-123" data-mp="1vmmmpf9wd5y4">
                <h3 className="text-xl md:text-[1.6rem] font-semibold text-white mb-4 leading-snug" data-id="element-124" data-mp="35kmdve9hfq3t">
                  Tu información de contacto
                </h3>

                <div className="flex items-start gap-3 rounded-lg border border-white/10 bg-white/[0.02] px-4 py-3.5 mb-8" data-id="element-125" data-mp="39nj1jjdvqcem">
                  <Lock className="h-4 w-4 text-gold shrink-0 mt-0.5" strokeWidth={2} data-id="element-126" data-mp="3ejgfxk5l18or" />
                  <p className="text-[13px] text-white/70 font-normal leading-relaxed" data-id="element-127" data-mp="3oc5exfjy4u37">
                    <span className="font-normal text-white" data-id="element-128" data-mp="3tagorqa8r4lf">
                      Tu información está segura.
                    </span>{' '}
                    Tus datos son privados y solo se usan para comunicarnos
                    contigo sobre tu plan para reparar tu crédito.{' '}
                    <a href="/privacidad" className="font-medium text-white underline decoration-white/30 underline-offset-2 transition-colors hover:text-gold" data-id="element-129" data-mp="172movsmjandc">
                      Ver política de privacidad
                    </a>
                  </p>
                </div>

                <form onSubmit={e => {
              e.preventDefault();
              if (contactValid && !sending) sendLead();
            }} className="space-y-5" data-id="element-130" data-mp="1l3fkyhshhqik">
                  <div data-id="element-131" data-mp="1zqr2d0bck4ql">
                    <label htmlFor="qf-name" className={labelClass} data-id="element-132" data-mp="1zqimczjxgjb6">
                      Nombre y apellido
                    </label>
                    <input id="qf-name" name="fullName" type="text" autoComplete="name" required value={contact.name} onChange={e => setContact({
                  ...contact,
                  name: e.target.value
                })} placeholder="Eduardo Rodríguez" className={inputClass} data-id="element-133" data-mp="23rkmdilo4ze7" />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-id="element-134" data-mp="29ws999bxmmni">
                    <div data-id="element-135" data-mp="36jn6znk43r6g">
                      <label htmlFor="qf-phone" className={labelClass} data-id="element-136" data-mp="36n2iyhihok3n">
                        Número de teléfono
                      </label>
                      <input id="qf-phone" name="phone" type="tel" inputMode="tel" autoComplete="tel" required value={contact.phone} onChange={e => setContact({
                    ...contact,
                    phone: e.target.value
                  })} placeholder="+1 (407) 367-8672" className={inputClass} data-id="element-137" data-mp="2wudck11spwf6" />
                    </div>
                    <div data-id="element-138" data-mp="1dw7amnxwcqe0">
                      <label htmlFor="qf-email" className={labelClass} data-id="element-139" data-mp="1dvfenc0e0qn7">
                        Correo electrónico
                      </label>
                      <input id="qf-email" name="email" type="email" inputMode="email" autoComplete="email" required value={contact.email} onChange={e => setContact({
                    ...contact,
                    email: e.target.value
                  })} placeholder="tucorreo@email.com" className={inputClass} data-id="element-140" data-mp="zysihbg9e4ic" />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-id="element-141" data-mp="8ncuodoc4s5o">
                    <div data-id="element-142" data-mp="nfep3i7mysyb">
                      <label htmlFor="qf-country" className={labelClass} data-id="element-143" data-mp="indhwapbrsf5">
                        ¿Dónde vives?
                      </label>
                      <select id="qf-country" name="country" autoComplete="country-name" required value={contact.country} onChange={e => setContact({
                    ...contact,
                    country: e.target.value,
                    city: ''
                  })} className={`${inputClass} appearance-none cursor-pointer`} data-id="element-144" data-mp="wdqqmfa1jb5j">
                        <option value="" data-id="element-145" data-mp="34lvjigkk69n9">Selecciona una opción</option>
                        <option value="Estados Unidos" data-id="element-146" data-mp="39kozcfnbicql">Estados Unidos</option>
                        <option value="Puerto Rico" data-id="element-147" data-mp="3ekuit8jfosxl">Puerto Rico</option>
                      </select>
                    </div>
                    <div data-id="element-148" data-mp="3jiml8whpfxg1">
                      <label htmlFor="qf-city" className={labelClass} data-id="element-149" data-mp="3jj5uzpx7bfiz">
                        {contact.country === 'Estados Unidos' ? 'Estado' : 'Ciudad'}
                      </label>
                      <select id="qf-city" name="city" autoComplete="address-level2" required disabled={cityOptions.length === 0} value={contact.city} onChange={e => setContact({
                    ...contact,
                    city: e.target.value
                  })} className={`${inputClass} appearance-none cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed`} data-id="element-150" data-mp="2vu3e4kae5qpj">
                        <option value="" data-id="element-151" data-mp="gasu4a66lcmj">
                          {cityOptions.length === 0 ? 'Elige dónde vives primero' : 'Selecciona una opción'}
                        </option>
                        {cityOptions.map(city => <option key={city} value={city} data-id="element-152" data-mp="1hpc61oogwva">
                            {city}
                          </option>)}
                      </select>
                    </div>
                  </div>

                  {submissionError && <p role="alert" className="rounded-lg border border-red-400/30 bg-red-400/10 px-4 py-3 text-sm text-red-100" data-id="element-153" data-mp="3jo3ra3yoncv9">
                      {submissionError}
                    </p>}

                  <div className="pt-3 flex items-center justify-between gap-4" data-id="element-154" data-mp="3a22ql9ywiktn">
                    <button type="button" onClick={() => setStep(s => Math.max(s - 1, 0))} disabled={sending} className="inline-flex items-center gap-2 text-sm text-white/65 hover:text-white transition-colors disabled:cursor-not-allowed disabled:opacity-40" data-id="element-155" data-mp="2w2jksntkblki">
                      <ArrowLeft className="h-4 w-4" data-id="element-156" data-mp="3su51s5srx8wi" />
                      Atrás
                    </button>
                    <button type="submit" disabled={!contactValid || sending} className="inline-flex items-center gap-2 bg-gold hover:bg-gold-light text-black font-semibold text-[15px] px-7 py-3.5 rounded-lg transition-colors duration-200 disabled:opacity-25 disabled:cursor-not-allowed disabled:hover:bg-gold" data-id="element-157" data-mp="3j2he8fzc8c0b">
                      {sending ? <>
                          <Loader2 className="h-4 w-4 animate-spin" data-id="element-158" data-mp="1v4gbo7lv320w" />
                          Enviando...
                        </> : <>
                          Enviar y continuar
                          <ArrowRight className="h-4 w-4" data-id="element-159" data-mp="1h4yrk5jigcxv" />
                        </>}
                    </button>
                  </div>
                </form>
              </motion.div>}

            {/* Success step */}
            {submitted && <motion.div key="result" initial={{
            opacity: 0,
            scale: 0.98
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.25,
            ease: [0.23, 1, 0.32, 1]
          }} className="text-center py-4" data-id="element-160" data-mp="1r0s59s8yuch8">
                <motion.div initial={{
              scale: 0.6,
              opacity: 0
            }} animate={{
              scale: 1,
              opacity: 1
            }} transition={{
              duration: 0.25,
              ease: [0.23, 1, 0.32, 1]
            }} className="h-16 w-16 rounded-full bg-emerald-500 ring-8 ring-emerald-500/15 flex items-center justify-center mx-auto mb-6" data-id="element-161" data-mp="zweex6asu3su">
                  <Check className="h-8 w-8 text-white" strokeWidth={3} data-id="element-162" data-mp="1ska8w38zhhlx" />
                </motion.div>

                <h3 className="text-xl md:text-[1.6rem] font-semibold text-white mb-4 leading-snug" data-id="element-163" data-mp="3pck773fbpb7w">
                  ¡Felicidades, {contact.name.split(' ')[0]}! ¡Tu caso califica!
                </h3>
                <p className="text-base text-white/85 font-normal leading-relaxed mb-8 max-w-md mx-auto" data-id="element-164" data-mp="3ms5rx8a8ini9">
                  Solo falta un paso: elige el día y la hora de tu diagnóstico
                  de 40 minutos. Revisamos tu reporte contigo y sales con un
                  plan concreto.
                </p>

                <a href={bookingUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 bg-gold hover:bg-gold-light text-black font-semibold px-8 py-4 rounded-lg transition-colors duration-200 text-base w-full sm:w-auto" data-id="element-165" data-mp="383rnisx66qzl">
                  Agenda tu llamada con nosotros
                  <ArrowRight className="h-5 w-5" data-id="element-166" data-mp="3quirtg2avr74" />
                </a>

                <button onClick={resetForm} className="mt-8 inline-flex items-center gap-2 text-sm text-white/55 hover:text-white transition-colors" data-id="element-167" data-mp="2kdnich4c2j6h">
                  <ArrowLeft className="h-4 w-4" data-id="element-168" data-mp="1dmoepcuhhczb" />
                  Volver a empezar
                </button>
              </motion.div>}
          </AnimatePresence>
        </motion.div>
      </div>
    </section>;
}